import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { debounce } from '@theme/utilities';
import { formatMoney } from './offer-validation.js';

/**
 * Frequently bought together / upsell recommendations component.
 * Uses Shopify Product Recommendations API with cart-items fallback.
 * Survives section morphs by re-querying refs and using IntersectionObserver.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} recommendationsList - Container for recommendation cards.
 * @property {HTMLElement} [emptyState] - Message when no recommendations available.
 * @property {HTMLElement} [title] - Section title element.
 *
 * @extends {Component<Refs>}
 */
class UpsellRecommendationsComponent extends Component {
  static observedAttributes = ['data-product-id'];

  /** @type {object[]} */
  #recommendations = [];

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {string[]} */
  #cartProductIds = [];

  /** @type {boolean} */
  #expired = false;

  /** @type {boolean} */
  #connected = false;

  /** @type {boolean} */
  #retried = false;

  /** @type {IntersectionObserver|null} */
  #visibilityObserver = null;

  /** @type {MutationObserver|null} */
  #dialogObserver = null;

  /** @type {number} */
  #lastRenderTime = 0;

  /** @type {number|null} */
  #retryTimer = null;

  /** @type {ResizeObserver|null} */
  #carouselResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#connected = true;
    this.#loadRecommendations();

    // Listen for cart events using the theme event system (debounced —
    // a single add-to-cart can emit several cartUpdate events)
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    this.addEventListener('offer:expired', this.#handleExpired);

    // Observe visibility to re-render after morph wipes content
    this.#setupVisibilityObserver();

    // Observe dialog open state for cart drawer (IntersectionObserver doesn't fire in closed dialogs)
    this.#setupDialogObserver();

    // If already inside an open dialog (e.g. after morph), schedule retries
    // to handle race conditions with cart state after first product add
    const dialog = this.closest('dialog');
    if (dialog?.open) {
      this.#scheduleRetries();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#connected = false;
    this.#abortController?.abort();
    if (this.#retryTimer) clearTimeout(this.#retryTimer);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#handleCartUpdate.cancel();
    this.removeEventListener('offer:expired', this.#handleExpired);
    this.#carouselResizeObserver?.disconnect();
    this.#carouselResizeObserver = null;
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#dialogObserver?.disconnect();
    this.#dialogObserver = null;
  }

  /**
   * Schedules progressive retries to handle race conditions where
   * /cart.js hasn't updated yet after a cart add operation.
   */
  #scheduleRetries() {
    const delays = [800];
    let i = 0;
    const tryNext = () => {
      if (!this.#connected || i >= delays.length) return;
      if (this.#recommendations.length > 0) return;
      this.#retryTimer = setTimeout(() => {
        if (!this.#connected || this.#recommendations.length > 0) return;
        this.#retried = false;
        this.#loadRecommendations();
        i++;
        tryNext();
      }, delays[i]);
    };
    tryNext();
  }

  /**
   * Reloads when the product-id attribute changes (e.g. after section morph).
   * @param {string} name
   * @param {string|null} oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-product-id' && oldValue !== newValue && this.#connected) {
      this.#retried = false;
      this.#loadRecommendations();
    }
  }

  /**
   * Sets up an IntersectionObserver to detect when the component becomes
   * visible (e.g. cart drawer opens). If morph wiped the rendered content,
   * re-renders from the cached recommendations array.
   */
  #setupVisibilityObserver() {
    this.#visibilityObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || !this.#connected) continue;

        const list = this.#getRecommendationsList();
        const isEmpty = !list || list.children.length === 0;

        if (isEmpty && this.#recommendations.length > 0) {
          // Content was wiped by morph, re-render from cache
          this.#render();
        } else if (isEmpty) {
          // No content — always try loading when becoming visible
          this.#retried = false;
          this.#loadRecommendations();
        }
      }
    }, { threshold: 0 });
    this.#visibilityObserver.observe(this);
  }

  /**
   * Observes the nearest dialog ancestor for open state changes.
   * When a dialog opens, reloads recommendations (IntersectionObserver
   * doesn't work for elements inside closed dialogs).
   */
  #setupDialogObserver() {
    const dialog = this.closest('dialog');
    if (!dialog) return;

    this.#dialogObserver = new MutationObserver(() => {
      if (dialog.open && this.#connected) {
        const list = this.#getRecommendationsList();
        const isEmpty = !list || list.children.length === 0;
        if (isEmpty || this.#recommendations.length === 0) {
          this.#retried = false;
          this.#loadRecommendations();
        } else {
          this.#render();
        }
      }
    });
    this.#dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  /**
   * Gets the recommendationsList element, re-querying if the ref is stale
   * (e.g. after a section morph replaced the DOM node).
   * @returns {HTMLElement|null}
   */
  #getRecommendationsList() {
    let list = this.refs.recommendationsList;
    if (!list || !list.isConnected) {
      list = this.querySelector('[ref="recommendationsList"]');
      if (list) this.refs.recommendationsList = list;
    }
    return list;
  }

  /**
   * Gets the title element, re-querying if stale.
   * @returns {HTMLElement|null}
   */
  #getTitle() {
    let el = this.refs.title;
    if (!el || !el.isConnected) {
      el = this.querySelector('[ref="title"]');
      if (el) this.refs.title = el;
    }
    return el;
  }

  /**
   * Gets the emptyState element, re-querying if stale.
   * @returns {HTMLElement|null}
   */
  #getEmptyState() {
    let el = this.refs.emptyState;
    if (!el || !el.isConnected) {
      el = this.querySelector('[ref="emptyState"]');
      if (el) this.refs.emptyState = el;
    }
    return el;
  }

  /**
   * Handles countdown expiry by disabling all add buttons.
   */
  #handleExpired = () => {
    this.#expired = true;
    this.querySelectorAll('.upsell-card__add-btn').forEach((btn) => {
      btn.setAttribute('disabled', '');
      btn.classList.add('upsell-card__add-btn--expired');
    });
  };

  /**
   * Loads product recommendations.
   */
  async #loadRecommendations() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const source = this.dataset.source || 'shopify';
    const limit = parseInt(this.dataset.limit || '4', 10);
    const fallbackCollectionHandle = this.dataset.fallbackCollection;

    await this.#fetchCartProductIds();

    // Use data-product-id if set, otherwise use first cart item
    let productId = this.dataset.productId;
    if (!productId && this.#cartProductIds.length > 0) {
      productId = this.#cartProductIds[0];
    }

    let products = [];

    if (source === 'shopify' && productId) {
      products = await this.#fetchShopifyRecommendations(productId, limit);
    }

    if (products.length === 0 && source !== 'manual') {
      products = await this.#fetchFromCartItems(limit);
    }

    if (products.length === 0 && fallbackCollectionHandle) {
      products = await this.#fetchCollectionProducts(fallbackCollectionHandle, limit);
    }

    products = products.filter(
      (p) => !this.#cartProductIds.includes(String(p.id))
    );

    this.#recommendations = products.slice(0, limit);
    this.#render();

    // Retry once after a delay if no results (handles race with cart session)
    if (this.#recommendations.length === 0 && !this.#retried && this.#connected) {
      this.#retried = true;
      setTimeout(() => {
        if (this.#connected) this.#loadRecommendations();
      }, 1500);
    }
  }

  /**
   * Fetches current cart product IDs.
   */
  async #fetchCartProductIds() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.#cartProductIds = (cart.items || []).map((item) => String(item.product_id));
    } catch {
      this.#cartProductIds = [];
    }
  }

  /**
   * Fetches recommendations from Shopify Product Recommendations API.
   * @param {string} productId
   * @param {number} limit
   * @returns {Promise<object[]>}
   */
  async #fetchShopifyRecommendations(productId, limit) {
    try {
      const url = `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`;
      const response = await fetch(url, { signal: this.#abortController?.signal });

      if (!response.ok) return [];

      const data = await response.json();
      return data.products || [];
    } catch (error) {
      if (error.name === 'AbortError') return [];
      console.error('Failed to fetch Shopify recommendations:', error);
      return [];
    }
  }

  /**
   * Fetches recommendations based on cart items.
   * @param {number} limit
   * @returns {Promise<object[]>}
   */
  async #fetchFromCartItems(limit) {
    const allRecs = [];

    for (const productId of this.#cartProductIds.slice(0, 3)) {
      const recs = await this.#fetchShopifyRecommendations(productId, limit);
      allRecs.push(...recs);
    }

    const uniqueRecs = [];
    const seen = new Set();
    for (const rec of allRecs) {
      if (!seen.has(rec.id)) {
        seen.add(rec.id);
        uniqueRecs.push(rec);
      }
    }

    return uniqueRecs;
  }

  /**
   * Fetches products from a collection as fallback.
   * @param {string} collectionHandle
   * @param {number} limit
   * @returns {Promise<object[]>}
   */
  async #fetchCollectionProducts(collectionHandle, limit) {
    try {
      const url = `/collections/${collectionHandle}/products.json?limit=${limit}`;
      const response = await fetch(url, { signal: this.#abortController?.signal });

      if (!response.ok) return [];

      const data = await response.json();
      return data.products || [];
    } catch (error) {
      if (error.name === 'AbortError') return [];
      return [];
    }
  }

  /**
   * Renders the recommendations list with optional carousel arrows.
   * Re-queries refs before rendering to survive section morphs.
   */
  #render() {
    const list = this.#getRecommendationsList();
    if (!list) return;

    const title = this.#getTitle();
    const emptyState = this.#getEmptyState();

    if (this.#recommendations.length === 0) {
      list.innerHTML = '';
      if (emptyState) emptyState.hidden = false;
      if (title) title.hidden = true;
      this.hidden = true;
      return;
    }

    this.hidden = false;
    if (emptyState) emptyState.hidden = true;
    if (title) title.hidden = false;

    const fragment = document.createDocumentFragment();

    for (const product of this.#recommendations) {
      const card = this.#createProductCard(product);
      fragment.appendChild(card);
    }

    list.innerHTML = '';
    list.appendChild(fragment);
    this.#lastRenderTime = Date.now();

    // Set up carousel arrows if inside cart drawer
    if (this.closest('.cart-drawer') || this.closest('dialog')) {
      this.#setupCarouselArrows(list);
    }
  }

  /**
   * Adds scroll arrows to the carousel and wires up scroll/resize listeners.
   * @param {HTMLElement} list
   */
  #setupCarouselArrows(list) {
    // Remove any existing arrows
    this.querySelectorAll('.upsell-carousel__arrow').forEach((el) => el.remove());

    const arrowLeft = document.createElement('button');
    arrowLeft.type = 'button';
    arrowLeft.className = 'upsell-carousel__arrow upsell-carousel__arrow--left';
    arrowLeft.setAttribute('aria-label', 'Scroll left');
    arrowLeft.hidden = true;
    arrowLeft.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>';

    const arrowRight = document.createElement('button');
    arrowRight.type = 'button';
    arrowRight.className = 'upsell-carousel__arrow upsell-carousel__arrow--right';
    arrowRight.setAttribute('aria-label', 'Scroll right');
    arrowRight.hidden = true;
    arrowRight.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';

    // Insert arrows before the list (inside the component wrapper)
    list.parentNode.insertBefore(arrowLeft, list);
    list.parentNode.insertBefore(arrowRight, list.nextSibling);

    // Wrap list parent in relative position for arrow placement
    const wrapper = list.parentNode;
    if (wrapper) wrapper.style.position = 'relative';

    const checkScroll = () => {
      const isScrollable = list.scrollWidth > list.clientWidth + 2;
      const atStart = list.scrollLeft <= 2;
      const atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;

      arrowLeft.hidden = !isScrollable || atStart;
      arrowRight.hidden = !isScrollable || atEnd;
    };

    const scrollByCard = (dir) => {
      const card = list.querySelector('.upsell-card');
      const step = card ? card.offsetWidth + 8 : 148;
      list.scrollBy({ left: dir * step, behavior: 'smooth' });
    };

    arrowLeft.addEventListener('click', () => scrollByCard(-1));
    arrowRight.addEventListener('click', () => scrollByCard(1));

    // Delay initial check — dialog open animation must complete before
    // scroll dimensions are valid. Use rAF + timeout for reliability.
    requestAnimationFrame(() => {
      setTimeout(checkScroll, 150);
    });
    list.addEventListener('scroll', checkScroll, { passive: true });
    this.#carouselResizeObserver?.disconnect();
    this.#carouselResizeObserver = new ResizeObserver(checkScroll);
    this.#carouselResizeObserver.observe(list);
  }

  /**
   * Creates a product recommendation card element.
   * @param {object} product
   * @returns {HTMLElement}
   */
  #createProductCard(product) {
    const card = document.createElement('div');
    card.className = 'upsell-card';

    const variant = product.variants?.[0];
    const price = variant?.price || product.price || 0;
    const compareAtPrice = variant?.compare_at_price || product.compare_at_price;
    const available = variant?.available !== false && product.available !== false;
    const imageUrl = product.featured_image
      || product.images?.[0]
      || product.image?.src
      || '';
    let imageSrc = '';
    if (typeof imageUrl === 'string' && imageUrl) {
      // Use Shopify CDN query params for reliable square crop
      const base = imageUrl.split('?')[0];
      imageSrc = `${base}?width=400&height=400&crop=center`;
    }

    let priceHtml = '';
    if (compareAtPrice && compareAtPrice > price) {
      priceHtml = `
        <s class="upsell-card__compare-price">${formatMoney(compareAtPrice)}</s>
        <span class="upsell-card__current-price">${formatMoney(price)}</span>
      `;
    } else {
      priceHtml = `<span class="upsell-card__current-price">${formatMoney(price)}</span>`;
    }

    const addLabel = available ? (this.dataset.tAdd || '+') : (this.dataset.tSoldOut || 'Sold out');
    const addIcon = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';

    card.innerHTML = `
      <div class="upsell-card__media">
        <a href="${product.url || `/products/${product.handle}`}">
          ${imageSrc ? `<img src="${imageSrc}" alt="${this.#escapeHtml(product.title)}" loading="lazy" width="200" height="200" class="upsell-card__image">` : ''}
        </a>
        ${available ? `<button class="upsell-card__quick-add" type="button" data-variant-id="${variant?.id || ''}" aria-label="${addLabel}">${addIcon}</button>` : ''}
      </div>
      <a href="${product.url || `/products/${product.handle}`}" class="upsell-card__title">
        ${this.#escapeHtml(product.title)}
      </a>
      <div class="upsell-card__price">
        ${priceHtml}
      </div>
    `;

    const addBtn = card.querySelector('.upsell-card__quick-add');
    if (addBtn) {
      if (this.#expired) {
        addBtn.setAttribute('disabled', '');
      } else {
        addBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (variant?.id) this.#addToCart(variant.id, addBtn);
        });
      }
    }

    return card;
  }

  /**
   * Gets the section IDs that need re-rendering after cart change.
   * @returns {string}
   */
  #getSectionIds() {
    const sections = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sections.add(el.dataset.sectionId);
      }
    });
    return Array.from(sections).join(',');
  }

  /**
   * Adds a product variant to cart.
   * @param {string|number} variantId
   * @param {HTMLButtonElement} button
   */
  async #addToCart(variantId, button) {
    button.setAttribute('disabled', '');
    button.classList.add('loading');

    try {
      const sectionIds = this.#getSectionIds();
      const body = {
        items: [{ id: variantId, quantity: 1 }],
      };

      if (sectionIds) {
        body.sections = sectionIds;
        body.sections_url = window.location.pathname;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const data = await response.json();

        const origHtml = button.innerHTML;
        button.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>';

        document.dispatchEvent(
          new CartAddEvent(data, 'upsell-recommendations', {
            source: 'upsell-recommendations-component',
            itemCount: 1,
            variantId: String(variantId),
            sections: data.sections || {},
          })
        );

        setTimeout(() => {
          button.innerHTML = origHtml;
          button.removeAttribute('disabled');
          button.classList.remove('loading');
        }, 1500);
      } else {
        button.removeAttribute('disabled');
        button.classList.remove('loading');
      }
    } catch {
      button.removeAttribute('disabled');
      button.classList.remove('loading');
    }
  }

  /**
   * Handles cart update events (debounced 400ms). Resets retry flag and
   * reloads once per burst of cartUpdate events; the 800ms #scheduleRetries
   * safety net still covers the "cart not settled yet" race on first add.
   */
  #handleCartUpdate = debounce(() => {
    if (!this.#connected) return;
    this.#retried = false;
    this.#loadRecommendations();
  }, 400);

  /**
   * Escapes HTML entities.
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('upsell-recommendations-component')) {
  customElements.define('upsell-recommendations-component', UpsellRecommendationsComponent);
}
